import { DragEndEvent } from "@dnd-kit/core";
import { AddLinkButton, AnnotationVariable, SortableListHolder } from "aic";
import { Form } from "react-bootstrap";
import { CompanionRow } from "./CompanionRow";
import { CompanionVariable, CompanionVariableType } from "./VariableDto";

/**
 * Represents the state of companion variables
 */
interface CompanionVariableState {
  companionVariables: CompanionVariable[];
}

/**
 * Props for the CompanionVariableControl component.
 */
interface CompanionVariableControlProps {
  variables: AnnotationVariable[];
  primaryDataset: string;
  companionVariables: CompanionVariable[];
  onChange: (state: CompanionVariableState) => void;
  disabled?: boolean;
}

/**
 * Component for managing companion variables with types.
 * Shows "+ Add Companion Variable" button initially, then reveals controls for each companion.
 * Maximum of 2 companions allowed, no duplicate types.
 *
 * @param props - Configuration for the companion variables control interface
 * @param props.variables - Shared ARS variables available for companion selection
 * @param props.primaryDataset - Dataset name from the primary variable, determines which variables are shown
 * @param props.companionVariables - Current companions with their selected variables and assigned types
 * @param props.onChange - Callback invoked when companions are added, removed, reordered, or modified
 * @param props.disabled - Disables all controls and prevents modifications when true
 */
export const CompanionVariableControl = ({
  variables,
  primaryDataset,
  companionVariables,
  onChange,
  disabled = false,
}: CompanionVariableControlProps): React.ReactElement => {
  const hasPrimaryVariable = Boolean(primaryDataset);

  /**
   * Get variables available for companion selection (from primary dataset only)
   */
  const variableOptions = variables
    .filter((variable) => variable.dataset === primaryDataset)
    .map((variable) => ({ value: variable.value }));
  const availableVariableOptions =
    variableOptions.length > 0 ? variableOptions : [{ value: "No variables available" }];

  /**
   * Get available companion types (exclude already used types)
   *
   * @param currentType - The current type of the companion being edited, excluded from used types to allow re-selection
   */
  const getAvailableTypes = (currentType?: CompanionVariableType): CompanionVariableType[] => {
    const allTypes: CompanionVariableType[] = ["Order", "Label", "Unit"];
    const usedTypes = companionVariables.map((c) => c.type).filter((t) => t !== currentType);

    return allTypes.filter((t) => !usedTypes.includes(t));
  };

  /**
   * Handler for adding a new companion variable
   */
  const handleAdd = () => {
    const availableTypes = getAvailableTypes();
    onChange({
      companionVariables: [...companionVariables, { variable: "", type: availableTypes[0]! }],
    });
  };

  /**
   * Removes a companion variable from the list based on its type.
   *
   * @param type - Companion type to remove (Order, Label, or Unit)
   */
  const handleRemove = (type: CompanionVariableType) => {
    onChange({
      companionVariables: companionVariables.filter((c) => c.type !== type),
    });
  };

  /**
   * Updates the selected variable for a specific companion type.
   * Ignores placeholder values and updates only the matching companion.
   *
   * @param type - Companion type whose variable selection is being changed
   * @param value - New variable name in format "dataset.variableName"
   */
  const handleVariableChange = (type: CompanionVariableType, value: string) => {
    if (value === "No variables available") return;

    onChange({
      companionVariables: companionVariables.map((c) =>
        c.type === type ? { ...c, variable: value } : c,
      ),
    });
  };

  /**
   * Changes a companion's type while preserving its variable selection.
   * Used when user selects a different type from the dropdown.
   *
   * @param oldType - Current type of the companion being modified
   * @param newType - New type to assign to the companion
   */
  const handleTypeChange = (oldType: CompanionVariableType, newType: CompanionVariableType) => {
    onChange({
      companionVariables: companionVariables.map((c) =>
        c.type === oldType ? { ...c, type: newType } : c,
      ),
    });
  };

  /**
   * Retrieves the selected variable name for a specific companion type.
   *
   * @param type - Companion type to look up
   * @returns Selected variable name for the companion, or empty string if not found
   */
  const getVariableValue = (type: CompanionVariableType): string => {
    return (
      companionVariables.find((c) => c.type === type)?.variable ??
      // istanbul ignore next -- Default only hit if companion not in array (impossible in practice)
      ""
    );
  };

  /**
   * Handles reordering companions via drag and drop.
   * Updates the companions array to reflect the new order.
   *
   * @param event - Drag end event containing active and target items
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    /* istanbul ignore next -- Defensive check: over is always set by the mock in tests */
    if (!over || active.id === over.id) return;

    const oldIndex = companionVariables.findIndex((c) => c.type === active.id);
    const newIndex = companionVariables.findIndex((c) => c.type === over.id);

    /* istanbul ignore next -- Defensive check: IDs are always valid companion types in practice */
    if (oldIndex === -1 || newIndex === -1) return;

    const newCompanions = [...companionVariables];
    const [removed] = newCompanions.splice(oldIndex, 1);
    newCompanions.splice(newIndex, 0, removed!);

    onChange({
      companionVariables: newCompanions,
    });
  };

  // Check if all existing companions have variables selected
  const allCompanionsHaveVariables = companionVariables.every((c) => c.variable !== "");

  const canAddMore =
    companionVariables.length < 2 && hasPrimaryVariable && allCompanionsHaveVariables && !disabled;
  const canMove = companionVariables.length > 1 && !disabled;

  return (
    <>
      {/* Show label only when companions exist */}
      {companionVariables.length > 0 && (
        <Form.Label className="mb-2">
          Companion Variable{companionVariables.length > 1 ? "s" : ""}
        </Form.Label>
      )}

      {companionVariables.length > 0 && (
        <SortableListHolder
          canMove={canMove}
          handleDragEnd={handleDragEnd}
          showBorder={false}
          items={companionVariables.map((companion, index) => ({
            id: companion.type,
            element: (
              <CompanionRow
                type={companion.type}
                index={index}
                variableOptions={availableVariableOptions}
                getVariableValue={getVariableValue}
                getAvailableTypes={getAvailableTypes}
                handleVariableChange={handleVariableChange}
                handleTypeChange={handleTypeChange}
                handleRemove={handleRemove}
                disabled={disabled}
              />
            ),
          }))}
        />
      )}

      {companionVariables.length < 2 && (
        <AddLinkButton
          label="Companion Variable"
          onClick={handleAdd}
          disabled={!canAddMore}
        />
      )}
    </>
  );
};

CompanionVariableControl.displayName = "CompanionVariableControl";
